import React, { useEffect, useState } from "react";
import "../css/customerLedger.css";
import PageHeader from "../components/customerLedger/PageHeader";
import DateFilterLedger from "../components/customerLedger/DateFilterLedger";
import ExportActions from "../components/customerLedger/ExportActions";
import EditDeleteActions from "../components/customerLedger/EditDeleteActions";
import { useParams } from "react-router-dom";
import {
  getCustomerLedger,
  getCustomerStats,
  getDailyLedgerReport,
} from "../services/reportService";
import { generateReport } from "../utils/report";
import { download, formatRows } from "../utils/reportformat";
import { generateDateTime } from "../utils/util";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import LedgerTableCombined from "../components/common/LedgerTable";
import LedgerKPI from "../components/common/StatsKPI";
import { encodeFilters } from "../components/filters/FilterUrl";

export default function CustomerLedger() {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);

  const [rawTransactions, setRawTransactions] = useState([]);
  const [customerLedgerData, setCustomerLedgerData] = useState(null);
  const [stats, setStats] = useState({});
  const [computeStats, setComputeStats] = useState({});

  const [editMode, setEditMode] = useState(false);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const f = params.get("f") || "";
    const result = {};
    for (const token of f.split(",")) {
      if (!token.trim()) continue;
      const [key, charge] = token.split(":");
      result[key] = charge || true;
    }
    return result;
  });

  useEffect(() => {
    if (id) getCustomerStatsAPI();
  }, [id]);

  useEffect(() => {
    if (id) getTransactionsByCustomer();
  }, [id, filters]);

  async function getTransactionsByCustomer() {
    setLoading(true);

    try {
      const response = await getDailyLedgerReport({
        customerId: id,
        from: fromDate,
        to: toDate,
        f: encodeFilters(filters),
      });
      if (response.success) {
        const otxns = response.originalTransactions;
        const t_txns = response?.transformedTransactions;
        setComputeStats(() => response?.stats || {});
        setRawTransactions(otxns);
        setCustomerLedgerData(t_txns);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }
  async function getCustomerStatsAPI() {
    const statsResponse = await getCustomerStats({ customerId: id });
    setStats(statsResponse || {});
  }

  const handleCSV = () => {
    const generateCSV = (data) => {
      const rows = formatRows(data.receive.rows);

      const headers = Object.keys(rows[0]).join(",");
      const values = rows.map((r) => Object.values(r).join(","));

      const csv = [headers, ...values].join("\n");

      download(csv, `customer-ledger-${generateDateTime()}.csv`);
    };

    generateCSV(customerLedgerData);
  };
  const handleXL = () => {
    const generateExcel = (data) => {
      const wb = XLSX.utils.book_new();

      // RECEIVE
      const receiveData = formatRows(data.receive.rows);
      const ws1 = XLSX.utils.json_to_sheet(receiveData);
      XLSX.utils.book_append_sheet(wb, ws1, "Receive");

      // GIVEN
      const givenData = formatRows(data.given.rows);
      const ws2 = XLSX.utils.json_to_sheet(givenData);
      XLSX.utils.book_append_sheet(wb, ws2, "Given");

      // BALANCE (single row)
      const balanceData = [formatRows([data.balance])[0]];
      const ws3 = XLSX.utils.json_to_sheet(balanceData);
      XLSX.utils.book_append_sheet(wb, ws3, "Balance");

      XLSX.writeFile(wb, `customer-ledger-${generateDateTime()}.xlsx`);
    };
    generateExcel(customerLedgerData);
  };
  const handlePrint = () => {
    const printContent = document.getElementById("report-section");

    const newWindow = window.open("", "", "width=1200,height=800");

    newWindow.document.write(`
    <html>
      <head>
        <title>Daily Report</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 10mm;
          }

          body {
            font-family: Arial;
            zoom: 0.75;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }

          th, td {
            border: 1px solid #000;
            padding: 4px;
            text-align: center;
            white-space: nowrap;
          }

          h2 {
            margin-top: 20px;
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
    </html>
  `);

    newWindow.document.close();
    newWindow.focus();

    setTimeout(() => {
      newWindow.print();
      newWindow.close();
    }, 500);
  };
  const handlePDF = async () => {
    const element = document.getElementById("pdf-wrapper");

    // SCALE DOWN TABLE FOR PDF
    element.style.transform = "scale(0.7)";
    element.style.transformOrigin = "top left";
    element.style.width = "1400px"; // important

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
    });

    // RESET STYLE AFTER CAPTURE
    element.style.transform = "scale(1)";
    element.style.width = "auto";

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("l", "mm", "a4");

    const pageWidth = 297;
    const pageHeight = 210;

    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`daily-report-${generateDateTime()}.pdf`);
  };

  return (
    <div className="bg-color">
      <PageHeader />
      <div className="page-wrapper px-3">
        <DateFilterLedger
          fromDate={fromDate}
          setFromDate={setFromDate}
          toDate={toDate}
          setToDate={setToDate}
          handleApplyBtn={getTransactionsByCustomer}
        />

        <EditDeleteActions edit={editMode} onEdit={setEditMode} />

        <ExportActions
          onExportCSV={handleCSV}
          onExportExcel={handleXL}
          onPrint={handlePrint}
          onExportPDF={handlePDF}
        />

        <div id="pdf-wrapper">
          <div id="report-section">
            <LedgerKPI stats={stats} />
            <LedgerTableCombined
              receiveRows={customerLedgerData?.receive?.rows || []}
              receiveTotals={customerLedgerData?.receive?.totals}
              givenRows={customerLedgerData?.given?.rows || []}
              givenTotals={customerLedgerData?.given?.totals}
              balance={customerLedgerData?.balance}
              originalData={rawTransactions}
              loading={loading}
              refresh={() => {
                getTransactionsByCustomer();
                getCustomerStatsAPI();
              }}
              editMode={editMode}
              setFilters={setFilters}
              stats={computeStats}
            />
          </div>
        </div>

        {/* {false && (
          <div className="action-bar-customer-ledger no-print">
            <button
              className="btn-action btn-submit"
              style={{ width: "300px" }}
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? "SUBMITTING..." : "SUBMIT"}
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
}
